// ============================================================
// TMR Portal Admin — aba Conta Corrente dentro do detalhe da empresa (14)
// ============================================================
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Field } from '../../components/ui';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useCurrentAccount } from '../../data/finance/queries';
import {
  accountStatusLabel,
  isAwaitingLegacyReplication,
  withdrawalSummary,
} from '../../data/finance/account';
import type { CurrentAccount } from '../../data/finance/contract';

/** Props for {@link CompanyCurrentAccountTab}. */
interface CompanyCurrentAccountTabProps {
  companyId: string;
  /** Takes the operator to the Contratos tab — the only corrective action this screen offers. */
  onGoToContracts: () => void;
}

/** A timestamp as the portal reads it. */
function moment(value: string): string {
  return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * The Empresa's conta corrente — its internal ledger account (14 §9).
 *
 * **It shows no money, and that is the design.** The V2 holds the account header only; balance and
 * statement stay in the legacy in this version (14 §2). A figure here would have to be invented, and a
 * screen that invents one is worse than a screen that says it does not have it.
 *
 * **It has no buttons either.** The withdrawal lock is a consequence of the contract, not an operator
 * decision — offering a switch would create a second source of truth for the same fact. The one action is
 * a link to Contratos, which is where the state actually changes.
 */
export function CompanyCurrentAccountTab({ companyId, onGoToContracts }: CompanyCurrentAccountTabProps) {
  const canRead = usePermission(PERMISSIONS.financeRead);
  const query = useCurrentAccount(canRead ? companyId : undefined);

  // Checked before the query's own states: without the permission there is no data to be loading, and
  // "sem permissão" must never be worded as "sem conta" — the account may well exist.
  if (!canRead) {
    return (
      <Notice tone="neutral">
        Você não tem permissão para ver a conta corrente desta empresa.
      </Notice>
    );
  }

  if (query.isLoading) return <Skeleton rows={4} />;

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const account = query.data;

  // Not an error: an Empresa only gets a conta corrente when its contract goes out for signature (14 §3).
  if (!account) {
    return (
      <EmptyState
        message="Esta empresa ainda não tem conta corrente."
        hint="A conta é aberta automaticamente quando o contrato é enviado para assinatura."
        action={
          <Btn variant="secondary" onClick={onGoToContracts}>
            Ver contratos
          </Btn>
        }
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <WithdrawalLock account={account} onGoToContracts={onGoToContracts} />

      <div className="pa-card" style={{ padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <Field label="Aberta em">{moment(account.openedAt)}</Field>
          <Field label="Situação">{accountStatusLabel(account.status)}</Field>
          <Field label="Saque liberado em">
            {account.withdrawalAllowedAt ? moment(account.withdrawalAllowedAt) : '—'}
          </Field>
          <Field label="Conta no legado">
            {isAwaitingLegacyReplication(account) ? (
              <span style={{ color: 'var(--fg-muted)' }}>ainda não replicada</span>
            ) : (
              <span style={{ fontFamily: 'var(--font-mono)' }}>#{account.legacyAccountId}</span>
            )}
          </Field>
          {account.legacyReplicatedAt && (
            <Field label="Replicada em">{moment(account.legacyReplicatedAt)}</Field>
          )}
        </div>
      </div>

      {isAwaitingLegacyReplication(account) && (
        <Notice tone="warning">
          A conta ainda não foi replicada para o sistema legado. Isso acontece sozinho assim que a empresa
          for exportada — enquanto não acontecer, o repasse não enxerga esta conta.
        </Notice>
      )}

      {/* Said once, plainly, so nobody reads this screen as the Empresa's financial position. */}
      <Notice tone="neutral">
        Saldo, extrato e movimentação continuam no sistema legado e não são exibidos aqui.
      </Notice>
    </div>
  );
}

/** The lock, as its own block: it is the one thing this screen exists to answer. */
function WithdrawalLock({
  account,
  onGoToContracts,
}: {
  account: CurrentAccount;
  onGoToContracts: () => void;
}) {
  const summary = withdrawalSummary(account);

  return (
    <Notice tone={summary.tone}>
      <strong>{summary.title}</strong> {summary.detail}{' '}
      {summary.linkToContracts && (
        <button
          type="button"
          onClick={onGoToContracts}
          style={{
            background: 'transparent',
            border: 0,
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Ver contratos
        </button>
      )}
    </Notice>
  );
}
